import { c as _c } from "react/compiler-runtime";
import Money from "@components/Money";
import { TipsOne } from "@icon-park/svg";
import { formatMoney, formatNumber, formatPercent } from "@justhome/common";
import { MaximumBudget, MaximumBudgetResult } from "@justhome/api-core";
import { Button, Icon, InfoIcon, InlineNotification, Table, Title } from "@justhome/react-ui";
import useLanguage from "@lib/hooks/useLanguage";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { getMeetingPath, getPartnerMeetingPath } from "@lib/routing";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { FC } from "react";
import MaximumBudgetEditModal from "../MaximumBudgetEditModal";
import MaximumBudgetResultRow from "./MaximumBudgetCardRow";
export interface MaximumBudgetCardProps {
  maximumBudget: MaximumBudget;
  className?: string;
}
const MaximumBudgetCard: FC<MaximumBudgetCardProps> = t0 => {
  const $ = _c(20);
  const {
    maximumBudget,
    className
  } = t0;
  const {
    t
  } = useTranslation("budget-check");
  const locale = useLanguage();
  const {
    partner
  } = usePartnerContext();
  const noResult = !maximumBudget.result?.maxPurchasePrice || maximumBudget.result?.maxPurchasePrice <= 0;
  let maximumBudgetLower = 0;
  let maximumBudgetUpper = 0;
  if (maximumBudget.result?.maxPurchasePrice) {
    maximumBudgetLower = Math.floor(maximumBudget.result.maxPurchasePrice * 0.9 / 1000) * 1000;
    maximumBudgetUpper = maximumBudget.result.maxPurchasePrice;
  }
  let t1;
  if ($[0] !== className) {
    t1 = clsx("border border-neutral-50 bg-neutral-0 shadow-4 rounded-2xs", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== maximumBudget || $[3] !== maximumBudgetLower || $[4] !== maximumBudgetUpper || $[5] !== noResult || $[6] !== t) {
    t2 = !noResult ? <><Title as="h3" className="text-h3 mb-3s text-balance">{t("maximum_budget_title")}</Title><div className="flex gap-3xs items-center mb-2s"><p className="text-h1 font-book text-primary-800">{formatNumber(maximumBudgetLower)} -{" "}<Money value={maximumBudgetUpper} /></p><InfoIcon size="lg"><MaximumBudgetCardResultExplanation {...maximumBudget} /></InfoIcon></div><div className="flex flex-col s:flex-row w-full justify-stretch gap-2xs m:gap-none m:divide-x divide-neutral-50"><MaximumBudgetResultRow label={t("result_equity_label")} value={<Money value={maximumBudget.equity} />} /><MaximumBudgetResultRow label={t("result_loan_amount_label")} value={<Money value={maximumBudget.result?.loanAmount} />} /><MaximumBudgetResultRow label={t("result_ancillary_cost_label")} info={<PurchaseCostInfo result={maximumBudget.result} />} value={<Money value={maximumBudget.result?.ancillaryCosts} />} /></div></> : <InlineNotification size="sm" type="info" icon={<Icon icon={TipsOne} size="24" />} className="text-body-2 mt-m flex text-info-700">{t("zero_result_title")}</InlineNotification>;
    $[2] = maximumBudget;
    $[3] = maximumBudgetLower;
    $[4] = maximumBudgetUpper;
    $[5] = noResult;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== locale || $[9] !== maximumBudget.hasSpecificProperty || $[10] !== partner || $[11] !== t) {
    t3 = partner ? <div className="flex flex-col justify-center items-center gap-s mt-m"><Button size="md" variant="primary" href={partner ? getPartnerMeetingPath(locale, partner.id) : getMeetingPath(locale)} className="mb-2xs" iconEnd="Right">{t("secondary_cta_label")}</Button></div> : <><div className="flex flex-row gap-m mt-m"><Button size="md" as="a" target="_blank" href="/vergleich" className="mb-2xs" iconEnd="Right">{t("cta_label")}</Button>{!maximumBudget.hasSpecificProperty && <Button size="md" variant="primary" href={getMeetingPath(locale)} className="mb-2xs" iconEnd="Right">{t("secondary_cta_label")}</Button>}</div><p className="text-title-1 text-neutral-675 text-opacity-60">{t("result_button_hint")}</p></>;
    $[8] = locale;
    $[9] = maximumBudget.hasSpecificProperty;
    $[10] = partner;
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <div className="p-s m:p-3s flex flex-col items-center">{t2}{t3}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MaximumBudgetEditModal />;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t1 || $[18] !== t4) {
    t6 = <section className={t1}>{t4}{t5}</section>;
    $[17] = t1;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
export default MaximumBudgetCard;
const PurchaseCostInfo = t0 => {
  const $ = _c(31);
  const {
    result
  } = t0;
  const {
    t
  } = useTranslation("budget-check");
  const locale = useLanguage();
  const t1 = result.propertyTransferTax / result.maxPurchasePrice * 100;
  let t2;
  if ($[0] !== locale || $[1] !== t1) {
    t2 = formatPercent(t1, locale);
    $[0] = locale;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `Grunderwerbssteuer (${t2})`;
  let T0;
  let T1;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== locale || $[4] !== result.maxPurchasePrice || $[5] !== result.notaryFee || $[6] !== result.propertyTransferTax || $[7] !== result.realEstateAgentFee || $[8] !== t || $[9] !== t3) {
    const costs = [{
      name: t3,
      amount: result.propertyTransferTax
    }, {
      name: `Notarkosten (${formatPercent(result.notaryFee / result.maxPurchasePrice * 100, locale)})`,
      amount: result.notaryFee
    }, {
      name: `Maklerprovision (${formatPercent(result.realEstateAgentFee / result.maxPurchasePrice * 100, locale)})`,
      amount: result.realEstateAgentFee
    }];
    t6 = "flex flex-col gap-s";
    let t8;
    if ($[16] !== t) {
      t8 = t("result_ancillary_cost_info");
      $[16] = t;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    if ($[18] !== t8) {
      t7 = <p>{t8}</p>;
      $[18] = t8;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    T1 = Table;
    t5 = 3;
    T0 = Table.Body;
    t4 = costs.map((cost, index) => <Table.Row key={index}><Table.Cell>{cost.name}</Table.Cell><Table.Cell className="text-right">{formatMoney(cost.amount, true, locale)}</Table.Cell></Table.Row>);
    $[3] = locale;
    $[4] = result.maxPurchasePrice;
    $[5] = result.notaryFee;
    $[6] = result.propertyTransferTax;
    $[7] = result.realEstateAgentFee;
    $[8] = t;
    $[9] = t3;
    $[10] = T0;
    $[11] = T1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    T0 = $[10];
    T1 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[20] !== T0 || $[21] !== t4) {
    t8 = <T0>{t4}</T0>;
    $[20] = T0;
    $[21] = t4;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== T1 || $[24] !== t5 || $[25] !== t8) {
    t9 = <T1 totalRows={t5}>{t8}</T1>;
    $[23] = T1;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t6 || $[28] !== t7 || $[29] !== t9) {
    t10 = <div className={t6}>{t7}{t9}</div>;
    $[27] = t6;
    $[28] = t7;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
const MaximumBudgetCardResultExplanation = t0 => {
  const $ = _c(35);
  const {
    equity,
    result
  } = t0;
  const {
    t
  } = useTranslation("budget-check");
  const locale = useLanguage();
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== equity || $[1] !== locale || $[2] !== result.ancillaryCosts || $[3] !== result.loanAmount || $[4] !== t) {
    const costs = [{
      name: "Eigenkapital",
      amount: equity
    }, {
      name: "Maximales Darlehen",
      amount: result.loanAmount,
      isPositive: true
    }, {
      name: "Kaufnebenkosten",
      amount: result.ancillaryCosts,
      isNegative: true
    }];
    t3 = "flex flex-col gap-s";
    let t5;
    if ($[11] !== t) {
      t5 = t("result_max_purchase_price_info");
      $[11] = t;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    if ($[13] !== t5) {
      t4 = <p>{t5}</p>;
      $[13] = t5;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    T1 = Table;
    t2 = 3;
    T0 = Table.Body;
    t1 = costs.map((cost, index) => <Table.Row key={index}><Table.Cell>{cost.name}</Table.Cell><Table.Cell className={clsx("text-center", cost.isNegative && "text-error-625", cost.isPositive && "text-success-625")}>{cost.isNegative && "-"}{cost.isPositive && "+"}</Table.Cell><Table.Cell className={clsx("text-right", cost.isNegative && "text-error-625", cost.isPositive && "text-success-625")}>{formatMoney(cost.amount, true, locale)}</Table.Cell></Table.Row>);
    $[0] = equity;
    $[1] = locale;
    $[2] = result.ancillaryCosts;
    $[3] = result.loanAmount;
    $[4] = t;
    $[5] = T0;
    $[6] = T1;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    T0 = $[5];
    T1 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Table.Row className="w-full h-xs" />;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Table.Cell>Maximales Kaufbudget</Table.Cell>;
    t7 = <Table.Cell />;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[18] !== locale || $[19] !== result.maxPurchasePrice) {
    t8 = formatMoney(result.maxPurchasePrice, true, locale);
    $[18] = locale;
    $[19] = result.maxPurchasePrice;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <Table.Row>{t6}{t7}<Table.Cell className="text-right">{t8}</Table.Cell></Table.Row>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== T0 || $[24] !== t1 || $[25] !== t9) {
    t10 = <T0>{t1}{t5}{t9}</T0>;
    $[23] = T0;
    $[24] = t1;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== T1 || $[28] !== t10 || $[29] !== t2) {
    t11 = <T1 totalRows={t2}>{t10}</T1>;
    $[27] = T1;
    $[28] = t10;
    $[29] = t2;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t3 || $[33] !== t4) {
    t12 = <div className={t3}>{t4}{t11}</div>;
    $[31] = t11;
    $[32] = t3;
    $[33] = t4;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};